import React, { useState } from 'react';
import { Box, Typography, Button, Drawer, IconButton, useTheme, useMediaQuery, Container, Grid, Card, CardContent } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronDown, ChevronRight, Lightbulb, ArrowRight, Menu, Users, Rocket, CheckCircle, ExternalLink, GraduationCap, BookOpen, Calendar, Gift, IndianRupee, UserCheck, MessageCircle } from 'lucide-react';

const PRIMARY = '#10b981';
const PRIMARY_HOVER = '#059669';
const PRIMARY_LIGHT = '#ECFDF5';
const BG = '#F8FAFC';
const CARD_BG = '#FFFFFF';
const TEXT_DARK = '#1E293B';
const TEXT_MUTED = '#64748B';
const BORDER = '#E5E7EB';
const MENU_TITLE_FONT = '"Poppins", "Inter", sans-serif';
const MENU_SECTION_FONT = '"Poppins", "Inter", sans-serif';
const SUBMENU_FONT = '"Inter", "Segoe UI", sans-serif';

type MenuSection = {
  id: string;
  label: string;
  children: { id: string; label: string }[];
};

const SIDEBAR_SECTIONS: MenuSection[] = [
  {
    id: 'intro-genai',
    label: 'Introduction to Generative AI',
    children: [
      { id: 'what-is-ai', label: 'What is Artificial Intelligence?' },
      { id: 'what-is-genai', label: 'What is Generative AI?' },
      { id: 'how-genai-works', label: 'How Generative AI Works (Simple View)' },
      { id: 'genai-use-cases', label: 'Real-World Use Cases of GenAI' },
      { id: 'limitations-responsible', label: 'Limitations & Responsible AI' },
    ],
  },
  {
    id: 'chat-models-llm',
    label: 'Chat Models & LLM Basics',
    children: [
      { id: 'what-is-chat-model', label: 'What is a Chat Model?' },
      { id: 'llms-explained', label: 'Large Language Models (LLMs) Explained' },
      { id: 'tokens-prompts-responses', label: 'Tokens, Prompts & Responses' },
      { id: 'context-memory', label: 'Context & Memory in Chat Models' },
      { id: 'popular-chat-models', label: 'Popular Chat Models (GPT, Claude, Gemini, LLaMA)' },
    ],
  },
  {
    id: 'chat-conversations',
    label: 'Chat Conversations',
    children: [
      { id: 'what-is-chat-conversation', label: 'What is a Chat Conversation?' },
      { id: 'single-vs-multi-turn', label: 'Single-Turn vs Multi-Turn Conversations' },
      { id: 'system-user-assistant', label: 'System, User & Assistant Roles' },
      { id: 'conversation-flow', label: 'Conversation Flow & Context Handling' },
      { id: 'best-practices-conversations', label: 'Best Practices for AI Conversations' },
    ],
  },
  {
    id: 'prompt-engineering',
    label: 'Prompt Engineering Fundamentals',
    children: [
      { id: 'what-is-prompt', label: 'What is a Prompt?' },
      { id: 'types-of-prompts', label: 'Types of Prompts' },
      { id: 'prompt-structure', label: 'Prompt Structure (Instruction, Context, Output)' },
      { id: 'good-vs-bad-prompts', label: 'Examples of Good vs Bad Prompts' },
      { id: 'common-prompt-mistakes', label: 'Common Prompt Mistakes' },
    ],
  },
  {
    id: 'chatbots',
    label: 'Introduction to Chatbots',
    children: [
      { id: 'what-is-chatbot', label: 'What is a Chatbot?' },
      { id: 'rule-vs-ai-chatbots', label: 'Rule-Based vs AI-Based Chatbots' },
      { id: 'chatbot-use-cases', label: 'Chatbot Use Cases (Support, Sales, Education)' },
      { id: 'chatbot-architecture', label: 'Chatbot Architecture (High Level)' },
      { id: 'popular-chatbots', label: 'Examples of Popular Chatbots' },
    ],
  },
  {
    id: 'rag',
    label: 'Introduction to RAG',
    children: [
      { id: 'why-llms-need-rag', label: 'Why LLMs Need RAG' },
      { id: 'what-is-rag', label: 'What is Retrieval Augmented Generation?' },
      { id: 'rag-flow', label: 'Documents → Search → AI Response (Simple Flow)' },
      { id: 'rag-use-cases', label: 'RAG Use Cases (Company Docs, Policies, FAQs)' },
      { id: 'rag-vs-finetuning', label: 'RAG vs Fine-Tuning (Conceptual)' },
    ],
  },
  {
    id: 'ai-agents',
    label: 'Introduction to AI Agents',
    children: [
      { id: 'what-is-agent', label: 'What is an AI Agent?' },
      { id: 'agent-vs-chatbot', label: 'Agent vs Chatbot' },
      { id: 'tools-memory-planning', label: 'Tools, Memory & Planning (Simple Explanation)' },
      { id: 'agent-examples', label: 'Agent Examples (Auto-reply, Data Lookup, Automation)' },
      { id: 'agent-use-cases', label: 'Real-World Agent Use Cases' },
    ],
  },
  {
    id: 'ai-providers',
    label: 'AI Providers & Ecosystem',
    children: [
      { id: 'providers-overview', label: 'Providers Overview' },
      { id: 'what-is-provider', label: 'What is an AI Provider?' },
      { id: 'how-providers-power-apps', label: 'How AI Providers Power Applications' },
      { id: 'types-of-providers', label: 'Types of Providers' },
      { id: 'direct-llm-providers', label: 'Direct LLM Providers (OpenAI, Google, Anthropic, Meta)' },
      { id: 'third-party-providers', label: 'Third-Party LLM Providers (OpenRouter, Groq, Together, Azure)' },
    ],
  },
  {
    id: 'choosing-ai',
    label: 'Choosing the Right AI',
    children: [
      { id: 'which-ai-for-use-case', label: 'Which AI is Best for Which Use Case?' },
      { id: 'cost-vs-performance', label: 'Cost vs Performance' },
      { id: 'privacy-data-safety', label: 'Privacy & Data Safety' },
      { id: 'open-vs-closed', label: 'Open-Source vs Closed Models' },
    ],
  },
  {
    id: 'real-world-apps',
    label: 'AI for Everyone – Real-World Applications',
    children: [
      { id: 'ai-for-students', label: 'AI for Students' },
      { id: 'ai-for-teachers', label: 'AI for Teachers' },
      { id: 'ai-for-businesses', label: 'AI for Businesses' },
      { id: 'ai-for-content-creators', label: 'AI for Content Creators' },
      { id: 'ai-for-developers', label: 'AI for Developers (Overview)' },
    ],
  },
  {
    id: 'getting-started',
    label: 'Getting Started (No Coding Required)',
    children: [
      { id: 'ai-without-coding', label: 'Using AI Tools Without Coding' },
      { id: 'ai-tools-daily-work', label: 'AI Tools for Daily Work' },
      { id: 'ai-tools-learning', label: 'AI Tools for Learning & Productivity' },
      { id: 'next-steps', label: 'Next Steps in AI Learning' },
    ],
  },
];

type SectionLandingContent = {
  title: string;
  intro: string;
  whatYouWillLearn: string[];
  whyItMatters: string;
  actionPrompt: string;
  startWithTopicId: string | null;
  startWithLabel: string;
  tip: string;
};

const SECTION_LANDING: Record<string, SectionLandingContent> = {
  'intro-genai': {
    title: 'Introduction to Generative AI',
    intro:
      'Start your AI journey by understanding what Artificial Intelligence and Generative AI are, how they work in simple terms, where they are used in the real world, and why responsible use matters.',
    whatYouWillLearn: [
      'What is Artificial Intelligence?',
      'What is Generative AI?',
      'How Generative AI works (simple view)',
      'Real-world use cases of GenAI',
      'Limitations and responsible AI',
    ],
    whyItMatters:
      'A clear foundation helps you use AI confidently and safely in daily life, studies, and work.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'what-is-ai',
    startWithLabel: 'What is Artificial Intelligence?',
    tip: 'Complete this section first to get the big picture before diving into chat models and prompts.',
  },
  'chat-models-llm': {
    title: 'Chat Models & LLM Basics',
    intro:
      'Learn how ChatGPT-like systems work: what chat models and Large Language Models are, how tokens and prompts turn into responses, and how context and memory shape the conversation.',
    whatYouWillLearn: [
      'What is a chat model?',
      'Large Language Models (LLMs) explained',
      'Tokens, prompts and responses',
      'Context and memory in chat models',
      'Popular chat models (GPT, Claude, Gemini, LLaMA)',
    ],
    whyItMatters:
      'Understanding these basics makes it easier to choose the right tool and get better results from AI.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'what-is-chat-model',
    startWithLabel: 'What is a Chat Model?',
    tip: 'Think of tokens as small pieces of text; shorter, clearer prompts often work better.',
  },
  'chat-conversations': {
    title: 'Chat Conversations',
    intro:
      'See how humans interact with AI through chat: single-turn vs multi-turn conversations, the roles of system, user, and assistant messages, and how to keep context and flow clear.',
    whatYouWillLearn: [
      'What is a chat conversation?',
      'Single-turn vs multi-turn conversations',
      'System, user and assistant roles',
      'Conversation flow and context handling',
      'Best practices for AI conversations',
    ],
    whyItMatters:
      'Good conversation design leads to more useful, consistent, and reliable AI interactions.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'what-is-chat-conversation',
    startWithLabel: 'What is a Chat Conversation?',
    tip: 'In multi-turn chats, earlier messages set the context—keep them relevant and concise.',
  },
  'prompt-engineering': {
    title: 'Prompt Engineering Fundamentals',
    intro:
      'Learn how to talk to AI effectively: what a prompt is, types and structure of prompts, examples of good vs bad prompts, and common mistakes to avoid.',
    whatYouWillLearn: [
      'What is a prompt?',
      'Types of prompts',
      'Prompt structure (instruction, context, output)',
      'Examples of good vs bad prompts',
      'Common prompt mistakes',
    ],
    whyItMatters:
      'Better prompts mean better answers—no coding required, just clearer instructions and context.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'what-is-prompt',
    startWithLabel: 'What is a Prompt?',
    tip: 'Be specific about what you want (format, length, tone) and give enough context.',
  },
  chatbots: {
    title: 'Introduction to Chatbots',
    intro:
      'Explore practical AI usage through chatbots: what they are, rule-based vs AI-based types, use cases in support, sales and education, high-level architecture, and popular examples.',
    whatYouWillLearn: [
      'What is a chatbot?',
      'Rule-based vs AI-based chatbots',
      'Chatbot use cases (support, sales, education)',
      'Chatbot architecture (high level)',
      'Examples of popular chatbots',
    ],
    whyItMatters:
      'Chatbots are where most people first meet AI; understanding them helps you use and evaluate them.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'what-is-chatbot',
    startWithLabel: 'What is a Chatbot?',
    tip: 'AI chatbots can handle open-ended questions; rule-based ones are best for fixed menus and flows.',
  },
  rag: {
    title: 'Introduction to RAG (Retrieval Augmented Generation)',
    intro:
      'Learn how AI can answer from your own data: why LLMs need RAG, what retrieval augmented generation is, the simple flow from documents to search to AI response, use cases, and how it differs from fine-tuning.',
    whatYouWillLearn: [
      'Why LLMs need RAG',
      'What is Retrieval Augmented Generation?',
      'Documents → Search → AI response (simple flow)',
      'RAG use cases (company docs, policies, FAQs)',
      'RAG vs fine-tuning (conceptual)',
    ],
    whyItMatters:
      'RAG is how you get AI to use your documents and knowledge without retraining the model.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'why-llms-need-rag',
    startWithLabel: 'Why LLMs Need RAG',
    tip: 'RAG is ideal when you have up-to-date or private data that the model was not trained on.',
  },
  'ai-agents': {
    title: 'Introduction to AI Agents',
    intro:
      'Discover AI that can think and act: what an agent is, how it differs from a chatbot, the roles of tools, memory and planning, and real-world examples and use cases.',
    whatYouWillLearn: [
      'What is an AI agent?',
      'Agent vs chatbot',
      'Tools, memory and planning (simple explanation)',
      'Agent examples (auto-reply, data lookup, automation)',
      'Real-world agent use cases',
    ],
    whyItMatters:
      'Agents can take actions (search, send emails, look up data), making AI more useful for workflows.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'what-is-agent',
    startWithLabel: 'What is an AI Agent?',
    tip: 'Agents combine an LLM with tools and memory to do multi-step tasks.',
  },
  'ai-providers': {
    title: 'AI Providers & Ecosystem',
    intro:
      'Understand where AI comes from: what an AI provider is, how providers power applications, types of providers, and the main direct and third-party LLM options (OpenAI, Google, Anthropic, Meta, OpenRouter, Groq, Together, Azure).',
    whatYouWillLearn: [
      'Providers overview',
      'What is an AI provider?',
      'How AI providers power applications',
      'Types of providers',
      'Direct LLM providers (OpenAI, Google, Anthropic, Meta)',
      'Third-party LLM providers (OpenRouter, Groq, Together, Azure)',
    ],
    whyItMatters:
      'Choosing the right provider affects cost, performance, privacy, and which models you can use.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'providers-overview',
    startWithLabel: 'Providers Overview',
    tip: 'Third-party providers often let you switch between many models through one API.',
  },
  'choosing-ai': {
    title: 'Choosing the Right AI',
    intro:
      'Make better decisions about which AI to use: matching use cases to models, balancing cost and performance, considering privacy and data safety, and comparing open-source vs closed models.',
    whatYouWillLearn: [
      'Which AI is best for which use case?',
      'Cost vs performance',
      'Privacy and data safety',
      'Open-source vs closed models',
    ],
    whyItMatters:
      'The "best" AI depends on your task, budget, and how much you care about data control.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'which-ai-for-use-case',
    startWithLabel: 'Which AI is Best for Which Use Case?',
    tip: 'Start with one model you can access easily, then compare others for specific needs.',
  },
  'real-world-apps': {
    title: 'AI for Everyone – Real-World Applications',
    intro:
      'See how AI applies to different roles—students, teachers, businesses, content creators, and developers—with a non-technical focus so everyone can find relevant use cases.',
    whatYouWillLearn: [
      'AI for students',
      'AI for teachers',
      'AI for businesses',
      'AI for content creators',
      'AI for developers (overview)',
    ],
    whyItMatters:
      'Real-world examples show where AI can save time, improve quality, and support learning and work.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'ai-for-students',
    startWithLabel: 'AI for Students',
    tip: 'Use AI to draft and brainstorm, but always verify facts and do your own thinking.',
  },
  'getting-started': {
    title: 'Getting Started (No Coding Required)',
    intro:
      'Build confidence with AI without writing code: using AI tools in daily work, learning and productivity, and clear next steps for your AI learning journey.',
    whatYouWillLearn: [
      'Using AI tools without coding',
      'AI tools for daily work',
      'AI tools for learning and productivity',
      'Next steps in AI learning',
    ],
    whyItMatters:
      'You can benefit from AI today using chat tools, writing assistants, and productivity apps.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'ai-without-coding',
    startWithLabel: 'Using AI Tools Without Coding',
    tip: 'Pick one or two tools, use them every day for real tasks, then explore more.',
  },
};

function SectionLandingView({
  content,
  onStartWithTopic,
  progress,
  activeId,
}: {
  content: SectionLandingContent;
  onStartWithTopic: (topicId: string) => void;
  progress: number;
  activeId: string | null;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: MENU_SECTION_FONT,
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 2,
        }}
      >
        {content.title}
      </Typography>

      <Typography
        sx={{
          color: TEXT_MUTED,
          lineHeight: 1.8,
          mb: 3,
          fontSize: '1rem',
        }}
      >
        {content.intro}
      </Typography>

      <Typography
        component="h2"
        sx={{
          fontSize: '1.2rem',
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 1.5,
        }}
      >
        What You Will Learn
      </Typography>
      <Box component="ul" sx={{ pl: 2.5, mb: 3, '& li': { color: TEXT_MUTED, lineHeight: 1.9 } }}>
        {content.whatYouWillLearn.map((item, i) => (
          <motion.li
            key={item}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.05 * i }}
          >
            {item}
          </motion.li>
        ))}
      </Box>

      <Typography
        component="h2"
        sx={{
          fontSize: '1.2rem',
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 1.5,
        }}
      >
        Why This Section Matters
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.8, mb: 3 }}>
        {content.whyItMatters}
      </Typography>

      <Typography sx={{ color: TEXT_DARK, fontWeight: 500, mb: 2 }}>
        {content.actionPrompt}
      </Typography>

      {content.startWithTopicId && (
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} style={{ display: 'inline-block' }}>
          <Button
            variant="contained"
            endIcon={<ArrowRight size={18} />}
            onClick={() => onStartWithTopic(content.startWithTopicId!)}
            sx={{
              bgcolor: PRIMARY,
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '12px',
              px: 2.5,
              py: 1.25,
              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
              '&:hover': { bgcolor: PRIMARY_HOVER },
            }}
          >
            Start with: {content.startWithLabel} →
          </Button>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        style={{ marginTop: 32 }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            p: 2,
            borderRadius: '12px',
            bgcolor: PRIMARY_LIGHT,
            border: `1px solid ${BORDER}`,
          }}
        >
          <Lightbulb size={22} color={PRIMARY} style={{ flexShrink: 0, marginTop: 2 }} />
          <Box>
            <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>
              Tip
            </Typography>
            <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.7, fontSize: '0.9rem' }}>
              {content.tip}
            </Typography>
          </Box>
        </Box>
      </motion.div>

      <Box sx={{ mt: 3 }}>
        <Box sx={{ height: 8, bgcolor: BORDER, borderRadius: 4, overflow: 'hidden' }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            style={{ height: '100%', backgroundColor: PRIMARY, borderRadius: 4 }}
          />
        </Box>
      </Box>
    </motion.div>
  );
}

const REGISTRATION_FORM_URL = 'https://forms.gle/fk4fPg9nReVwjz327';

const PRIMARY_BLUE = '#2563EB';
const PRIMARY_BLUE_LIGHT = '#EAF2FF';

function HeroRegistrationSection() {
  const navigate = useNavigate();
  return (
    <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh', pt: { xs: 7, md: 8 } }}>
      {/* TOP BANNER IMAGE */}
      <Box
        component="img"
        src="/ai-teacher-reviving-india.png"
        alt="AI Teacher Program - Reviving India Banner"
        sx={{
          width: '100%',
          height: 'auto',
          display: 'block',
        }}
      />

      {/* HERO SECTION - Content Left, Image Right */}
      <Box sx={{ py: { xs: 5, md: 8 }, px: { xs: 2, md: 4 }, bgcolor: '#fff', overflow: 'hidden' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} sx={{ alignItems: 'center' }}>
            {/* Left - Content */}
            <Grid size={{ xs: 12, md: 6 }}>
              <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Box sx={{ bgcolor: PRIMARY_BLUE_LIGHT, color: PRIMARY_BLUE, px: 2.5, py: 0.75, borderRadius: '20px', fontSize: '0.9rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Rocket size={16} />
                    AI Teacher Initiative
                  </Box>
                </Box>

                <Typography component="h1" sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' }, fontWeight: 800, lineHeight: 1.3, mb: 2, color: '#1e293b' }}>
                  Become an AI-Powered Teacher of Tomorrow
                </Typography>

                <Typography sx={{ fontSize: { xs: '1rem', md: '1.15rem' }, color: PRIMARY_BLUE, lineHeight: 1.6, mb: 3, fontWeight: 600 }}>
                  Build a Future-Ready Career with Generative AI, Agentic AI & Smart Teaching Tools
                </Typography>

                <Typography sx={{ fontSize: { xs: '0.95rem', md: '1rem' }, color: '#64748b', lineHeight: 1.8, mb: 3 }}>
                  Transform your teaching career with AI skills. Whether you are a school teacher, college faculty, student, or working professional — this program helps you switch to an AI Teacher career or upgrade your current role with AI.
                </Typography>

                <Box sx={{ mb: 3 }}>
                  {['No Coding Required', '3 Months Program', '₹500 per Month', 'Certificate + Placement Support', 'Personalized Learning Path'].map((item, i) => (
                    <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
                        <CheckCircle size={20} color="#22c55e" />
                        <Typography sx={{ color: '#1e293b', fontSize: '0.95rem', fontWeight: 500 }}>{item}</Typography>
                      </Box>
                    </motion.div>
                  ))}
                </Box>

                <Box sx={{ bgcolor: '#ecfdf5', border: '2px solid #22c55e', borderRadius: '14px', p: 2, mb: 3, display: 'inline-block' }}>
                  <Typography sx={{ color: '#16a34a', fontWeight: 700, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <GraduationCap size={22} /> Free Enrollment Open Now — Limited Seats!
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, flexWrap: 'wrap' }}>
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    <Button variant="contained" href={REGISTRATION_FORM_URL} target="_blank" rel="noopener noreferrer" endIcon={<ExternalLink size={20} />} sx={{ background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)', color: '#fff', fontSize: { xs: '1rem', md: '1.1rem' }, fontWeight: 700, textTransform: 'none', px: { xs: 4, md: 5 }, py: { xs: 1.5, md: 1.75 }, borderRadius: '14px', boxShadow: '0 8px 32px rgba(37, 99, 235, 0.35)', '&:hover': { background: 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)', boxShadow: '0 12px 40px rgba(37, 99, 235, 0.45)' } }}>
                      Register Now
                    </Button>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    <Button variant="contained" href={REGISTRATION_FORM_URL} target="_blank" rel="noopener noreferrer" sx={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', color: '#fff', fontSize: { xs: '1rem', md: '1.1rem' }, fontWeight: 700, textTransform: 'none', px: { xs: 4, md: 5 }, py: { xs: 1.5, md: 1.75 }, borderRadius: '14px', boxShadow: '0 8px 32px rgba(34, 197, 94, 0.35)', '&:hover': { background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', boxShadow: '0 12px 40px rgba(34, 197, 94, 0.45)' } }}>
                      Book for Free Demo Now
                    </Button>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    <Button variant="outlined" onClick={() => navigate('/ai-training-dashboard')} endIcon={<ArrowRight size={20} />} sx={{ borderColor: '#2563EB', borderWidth: 2, color: '#2563EB', fontSize: { xs: '1rem', md: '1.1rem' }, fontWeight: 700, textTransform: 'none', px: { xs: 4, md: 5 }, py: { xs: 1.5, md: 1.75 }, borderRadius: '14px', '&:hover': { borderColor: '#1D4ED8', borderWidth: 2, background: 'rgba(37, 99, 235, 0.06)' } }}>
                      View Training Dashboard
                    </Button>
                  </motion.div>
                </Box>
              </motion.div>
            </Grid>

            {/* Right - Two Images with Creative Design */}
            <Grid size={{ xs: 12, md: 6 }}>
              {/* Mobile: Full width large images stacked */}
              <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 3, mt: 2 }}>
                <Box
                  component="img"
                  src="/ai-teacher-hero.jpg"
                  alt="AI Teacher Program"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '16px',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                  }}
                />
                <Box
                  component="img"
                  src="/ai-teacher-hero-2.jpg"
                  alt="AI Teaching in Action"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '16px',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                  }}
                />
              </Box>

              {/* Desktop: Overlapping images */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, position: 'relative', height: 550, justifyContent: 'center', alignItems: 'center' }}>
                <Box sx={{ position: 'absolute', top: '10%', right: '5%', width: 120, height: 120, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(37, 99, 235, 0.05) 100%)', zIndex: 0 }} />
                <Box sx={{ position: 'absolute', bottom: '15%', left: '0%', width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(34, 197, 94, 0.05) 100%)', zIndex: 0 }} />
                
                <motion.div 
                  initial={{ opacity: 0, x: 50, rotate: 3 }} 
                  animate={{ opacity: 1, x: 0, rotate: 3 }} 
                  transition={{ duration: 0.6, delay: 0.2 }}
                  style={{ position: 'absolute', right: 0, top: '0%', zIndex: 1 }}
                >
                  <Box
                    component="img"
                    src="/ai-teacher-hero.jpg"
                    alt="AI Teacher Program"
                    sx={{
                      width: 420,
                      height: 'auto',
                      objectFit: 'contain',
                      borderRadius: '16px',
                      boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
                      border: '4px solid #fff',
                    }}
                  />
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: -50, rotate: -5 }} 
                  animate={{ opacity: 1, x: 0, rotate: -5 }} 
                  transition={{ duration: 0.6, delay: 0.4 }}
                  style={{ position: 'absolute', left: '0%', bottom: '5%', zIndex: 2 }}
                >
                  <Box
                    component="img"
                    src="/ai-teacher-hero-2.jpg"
                    alt="AI Teaching in Action"
                    sx={{
                      width: 380,
                      height: 'auto',
                      objectFit: 'contain',
                      borderRadius: '16px',
                      boxShadow: '0 25px 60px rgba(37, 99, 235, 0.25)',
                      border: '4px solid #fff',
                    }}
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 0.7 }}
                  style={{ position: 'absolute', right: '10%', bottom: '30%', zIndex: 3 }}
                >
                  <Box sx={{ 
                    bgcolor: '#fff', 
                    borderRadius: '50%', 
                    width: 90, 
                    height: 90, 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center', 
                    justifyContent: 'center',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                    border: '3px solid #2563EB'
                  }}>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#2563EB', lineHeight: 1 }}>3</Typography>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Months</Typography>
                  </Box>
                </motion.div>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* PROGRAM VISION */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 }, bgcolor: '#f8fafc' }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <motion.div initial={{ scale: 0.9 }} whileInView={{ scale: 1 }} viewport={{ once: true }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: PRIMARY_BLUE_LIGHT, px: 3, py: 1, borderRadius: '30px', mb: 3 }}>
                  <Users size={18} color={PRIMARY_BLUE} />
                  <Typography sx={{ color: PRIMARY_BLUE, fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Who Can Apply</Typography>
                </Box>
              </motion.div>
              <Typography component="h2" sx={{ 
                fontSize: { xs: '1.75rem', sm: '2rem', md: '2.75rem' }, 
                fontWeight: 800, 
                color: '#1e293b', 
                mb: 3,
                lineHeight: 1.2,
                '& span': { color: PRIMARY_BLUE }
              }}>
                Who Can Apply for the<br /><span>AI Teacher Career Program?</span>
              </Typography>
              <Typography sx={{ color: '#64748b', fontSize: { xs: '1rem', md: '1.15rem' }, maxWidth: 750, mx: 'auto', lineHeight: 1.8 }}>
                Designed for teachers, students, and working professionals who want to adopt AI skills, upgrade their current role, or switch to a future-ready AI-powered career.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {[
                { image: '/teacher-govt.png', label: 'Government Teachers' },
                { image: '/teacher-private.png', label: 'Private School Teachers' },
                { image: '/teacher-professor.png', label: 'College Professors' },
                { image: '/teacher-student.png', label: 'B.Ed / D.El.Ed Students' },
                { image: '/teacher-coaching.png', label: 'Coaching Teachers' },
                { image: '/teacher-professional.png', label: 'Working Professionals' },
                { image: '/teacher-ai-adopt.png', label: 'Anyone who wants to adopt AI' },
                { image: '/teacher-career-switch.png', label: 'Career Switchers' },
              ].map((item, i) => (
                <Grid size={{ xs: 6, sm: 4, md: 3 }} key={i}>
                  <motion.div whileHover={{ y: -8, scale: 1.02 }} transition={{ duration: 0.3 }}>
                    <Card sx={{ overflow: 'hidden', border: '1px solid #e5e7eb', boxShadow: 'none', '&:hover': { borderColor: PRIMARY_BLUE, boxShadow: '0 12px 40px rgba(37, 99, 235, 0.15)' } }}>
                      <Box sx={{ position: 'relative', height: 160, overflow: 'hidden' }}>
                        <Box
                          component="img"
                          src={item.image}
                          alt={item.label}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.3s ease',
                            '&:hover': { transform: 'scale(1.05)' }
                          }}
                        />
                        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '50%', background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)' }} />
                      </Box>
                      <Box sx={{ p: 2, textAlign: 'center', bgcolor: '#fff' }}>
                        <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>{item.label}</Typography>
                      </Box>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ textAlign: 'center', mt: 5, p: 4, bgcolor: PRIMARY_BLUE_LIGHT, borderRadius: '16px' }}>
              <Typography sx={{ fontSize: '1.2rem', color: '#1e293b', fontWeight: 600 }}>
                This course is not just about learning AI.<br />
                <span style={{ color: PRIMARY_BLUE, fontWeight: 800 }}>We transform you into a Job-Ready AI-Enabled Educator.</span>
              </Typography>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* WHAT IS AI TEACHER CAREER PROGRAM */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 }, bgcolor: '#f8fafc', overflow: 'hidden' }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Grid container spacing={6} sx={{ alignItems: 'center' }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography sx={{ color: PRIMARY_BLUE, fontWeight: 700, fontSize: '0.9rem', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BookOpen size={18} /> WHAT IS AI TEACHER CAREER PROGRAM?
                </Typography>
                <Typography component="h2" sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 800, color: '#1e293b', mb: 3 }}>
                  A 3-Month Career-Focused Course
                </Typography>
                <Typography sx={{ color: '#64748b', lineHeight: 1.8, mb: 3 }}>
                  The AI Teacher Career Program helps you switch your career to AI Education, upgrade your current teaching methods using AI, become a modern digital educator, and use AI tools in daily teaching, content creation, and productivity.
                </Typography>
                <Box sx={{ bgcolor: '#fff', p: 3, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                  {['Switch your career to AI Education', 'Upgrade your current teaching methods', 'Become a modern digital educator', 'Use AI tools in daily teaching & content creation'].map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                      <CheckCircle size={20} color="#22c55e" />
                      <Typography sx={{ color: '#1e293b', fontWeight: 500 }}>{item}</Typography>
                    </Box>
                  ))}
                </Box>
                <Typography sx={{ mt: 3, color: PRIMARY_BLUE, fontWeight: 700, fontSize: '1rem' }}>
                  Designed for AI for Everyone — even if you have zero technical background.
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                {/* Mobile: Full width large images */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 3, mt: 4 }}>
                  <Box
                    component="img"
                    src="/ai-program-1.jpg"
                    alt="AI Teacher Training"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '16px',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    }}
                  />
                  <Box
                    component="img"
                    src="/ai-program-2.jpg"
                    alt="AI Learning Session"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '16px',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    }}
                  />
                </Box>

                {/* Desktop: Overlapping images */}
                <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'relative', height: 450 }}>
                  <motion.div
                    initial={{ opacity: 0, x: 30, rotate: 3 }}
                    whileInView={{ opacity: 1, x: 0, rotate: 3 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    style={{ position: 'absolute', right: 0, top: 0, zIndex: 1 }}
                  >
                    <Box
                      component="img"
                      src="/ai-program-1.jpg"
                      alt="AI Teacher Training"
                      sx={{
                        width: 300,
                        height: 'auto',
                        borderRadius: '16px',
                        boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
                        border: '4px solid #fff',
                      }}
                    />
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: -30, rotate: -3 }}
                    whileInView={{ opacity: 1, x: 0, rotate: -3 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    style={{ position: 'absolute', left: 0, bottom: 0, zIndex: 2 }}
                  >
                    <Box
                      component="img"
                      src="/ai-program-2.jpg"
                      alt="AI Learning Session"
                      sx={{
                        width: 280,
                        height: 'auto',
                        borderRadius: '16px',
                        boxShadow: '0 25px 60px rgba(37, 99, 235, 0.25)',
                        border: '4px solid #fff',
                      }}
                    />
                  </motion.div>

                  <Box sx={{ position: 'absolute', top: '20%', left: '30%', width: 100, height: 100, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(37, 99, 235, 0.05) 100%)', zIndex: 0 }} />
                  <Box sx={{ position: 'absolute', bottom: '30%', right: '20%', width: 60, height: 60, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(34, 197, 94, 0.05) 100%)', zIndex: 0 }} />

                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.5 }}
                    style={{ position: 'absolute', right: '25%', bottom: '35%', zIndex: 3 }}
                  >
                    <Box sx={{ 
                      bgcolor: PRIMARY_BLUE, 
                      borderRadius: '50%', 
                      width: 100, 
                      height: 100, 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center', 
                      justifyContent: 'center',
                      boxShadow: '0 10px 30px rgba(37, 99, 235, 0.4)',
                      border: '3px solid #fff'
                    }}>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#fff', textAlign: 'center', lineHeight: 1.3 }}>Join<br/>AI for<br/>Everyone</Typography>
                    </Box>
                  </motion.div>
                </Box>
              </Grid>
            </Grid>
          </motion.div>
        </Container>
      </Box>



      {/* COURSE STRUCTURE */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography sx={{ color: PRIMARY_BLUE, fontWeight: 700, fontSize: '0.9rem', mb: 1 }}>
                <Calendar size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} /> COURSE STRUCTURE
              </Typography>
              <Typography component="h2" sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' }, fontWeight: 800, color: '#1e293b' }}>
                3 Months to Transform Your Career
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {[
                { month: 'Month 1', title: 'AI Foundations & Tools', color: PRIMARY_BLUE, items: ['Introduction to AI & GenAI', 'AI Tools for Teachers', 'Prompt Engineering Basics', 'AI for Content & Lesson Planning'] },
                { month: 'Month 2', title: 'Advanced AI + Practical', color: PRIMARY_BLUE, items: ['Agentic AI Concepts', 'AI Chatbot Creation', 'RAG Knowledge System', 'AI in Classroom Automation'] },
                { month: 'Month 3', title: 'Career + Job Readiness', color: PRIMARY_BLUE, items: ['Resume Building with AI', 'Interview Preparation', 'Live Projects', 'Job Guidance & Placement Support'] },
              ].map((m, i) => (
                <Grid size={{ xs: 12, md: 4 }} key={i}>
                  <motion.div whileHover={{ y: -8 }}>
                    <Card sx={{ height: '100%', border: '1px solid #e5e7eb', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', overflow: 'visible', borderRadius: '16px' }}>
                      <Box sx={{ bgcolor: '#fff', py: 3, px: 3, textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: '#1e293b' }}>{m.month}</Typography>
                        <Typography sx={{ fontSize: '1rem', color: '#1e293b', fontWeight: 600 }}>{m.title}</Typography>
                      </Box>
                      <CardContent sx={{ p: 3 }}>
                        {m.items.map((item, j) => (
                          <Box key={j} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, borderBottom: j < m.items.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                            <CheckCircle size={18} color={m.color} />
                            <Typography sx={{ color: '#1e293b', fontWeight: 500 }}>{item}</Typography>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* BONUS FEATURES */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 }, bgcolor: PRIMARY_BLUE_LIGHT }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography sx={{ color: PRIMARY_BLUE, fontWeight: 700, fontSize: '0.9rem', mb: 1 }}>
                <Gift size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} /> BONUS FEATURES
              </Typography>
              <Typography component="h2" sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' }, fontWeight: 800, color: '#1e293b' }}>
                What's Included
              </Typography>
            </Box>

            <Grid container spacing={2} justifyContent="center">
              {['AI Skills Training', 'Resume Building', 'Interview Preparation', 'Job Ready Training', 'Placement Assistance', 'Personalized Career Roadmap', 'Lifetime Community Support'].map((bonus, i) => (
                <Grid size={{ xs: 6, sm: 4, md: 'auto' }} key={i}>
                  <motion.div whileHover={{ scale: 1.05 }}>
                    <Box sx={{ bgcolor: '#fff', px: 3, py: 2, borderRadius: '12px', display: 'flex', alignItems: 'center', gap: 1.5, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                      <CheckCircle size={20} color="#22c55e" />
                      <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{bonus}</Typography>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* PROGRAM HIGHLIGHTS STATS */}
      <Box sx={{ py: { xs: 6, md: 8 }, px: { xs: 2, md: 4 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <Grid container spacing={3} justifyContent="center">
            {[
              { value: '11+', label: 'AI Modules' },
              { value: '50+', label: 'Practical Topics' },
              { value: '100%', label: 'Hands-on Learning' },
              { value: '3', label: 'Months Program' },
              { value: '₹500', label: 'Per Month' },
            ].map((stat, i) => (
              <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={i}>
                <motion.div whileHover={{ scale: 1.05 }}>
                  <Box sx={{ textAlign: 'center', p: 3, bgcolor: '#f8fafc', borderRadius: '16px' }}>
                    <Typography sx={{ fontSize: '2rem', fontWeight: 800, color: PRIMARY_BLUE }}>{stat.value}</Typography>
                    <Typography sx={{ color: '#64748b', fontWeight: 500 }}>{stat.label}</Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* WHO CAN JOIN */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 }, bgcolor: '#f8fafc' }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography sx={{ color: PRIMARY_BLUE, fontWeight: 700, fontSize: '0.9rem', mb: 1 }}>
                <Users size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} /> WHO CAN JOIN?
              </Typography>
              <Typography component="h2" sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' }, fontWeight: 800, color: '#1e293b' }}>
                This Course is Perfect For
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {['Primary School Teachers', 'Secondary School Teachers', 'College Faculty', 'B.Ed Students', 'Government Teachers', 'Private Teachers', 'Housewives interested in teaching', 'Students wanting AI career', 'Professionals switching to AI'].map((who, i) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                    <UserCheck size={24} color={PRIMARY_BLUE} />
                    <Typography sx={{ fontWeight: 600, color: '#1e293b' }}>{who}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography sx={{ color: '#22c55e', fontWeight: 700, fontSize: '1.1rem' }}>
                No coding. No technical background required.
              </Typography>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* PRICING & CTA */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 }, background: 'linear-gradient(135deg, #030712 0%, #0f172a 50%, #030712 100%)' }}>
        <Container maxWidth="md">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Box sx={{ textAlign: 'center', color: '#fff' }}>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 1, opacity: 0.9 }}>
                <IndianRupee size={16} style={{ verticalAlign: 'middle' }} /> AFFORDABLE CAREER INVESTMENT
              </Typography>
              <Typography component="h2" sx={{ fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 800, mb: 2 }}>
                Just ₹500 per Month
              </Typography>
              <Typography sx={{ fontSize: '1.2rem', opacity: 0.9, mb: 4 }}>
                Total Course Duration: 3 Months | Mode: Online + Practical Training | Level: Beginner to Advanced
              </Typography>

              <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '16px', p: 3, mb: 4, border: '1px dashed rgba(255,255,255,0.3)' }}>
                <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <Gift size={24} /> Free Enrollment Available (Limited Time)
                </Typography>
              </Box>

              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button variant="contained" href={REGISTRATION_FORM_URL} target="_blank" rel="noopener noreferrer" endIcon={<ExternalLink size={22} />} sx={{ background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)', color: '#fff', fontSize: '1.2rem', fontWeight: 800, textTransform: 'none', px: 6, py: 2, borderRadius: '16px', boxShadow: '0 8px 30px rgba(37, 99, 235, 0.4)', '&:hover': { background: 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)', boxShadow: '0 12px 40px rgba(37, 99, 235, 0.5)' } }}>
                  Register Now - Start Your AI Journey
                </Button>
              </motion.div>

              <Typography sx={{ mt: 3, opacity: 0.8, fontSize: '0.9rem' }}>
                We don't just teach AI — We make you Job Ready + Career Ready + Future Ready.
              </Typography>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* STUDENT FEEDBACK - YouTube Shorts */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 }, bgcolor: '#f8fafc' }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: PRIMARY_BLUE_LIGHT, px: 3, py: 1, borderRadius: '30px', mb: 2 }}>
                <MessageCircle size={18} color={PRIMARY_BLUE} />
                <Typography sx={{ color: PRIMARY_BLUE, fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Student Feedback</Typography>
              </Box>
              <Typography component="h2" sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' }, fontWeight: 800, color: '#1e293b' }}>
                What Our Students Say
              </Typography>
              <Typography sx={{ color: '#64748b', fontSize: { xs: '1rem', md: '1.1rem' }, maxWidth: 600, mx: 'auto', mt: 1 }}>
                Hear from our students who transformed their careers with the AI Teacher Program
              </Typography>
            </Box>

            <Grid container spacing={3} justifyContent="center">
              {[
                'EhETnfOjmWA',
                'hU_WiifcZH4',
                'BHEmRyrG1E4',
                'Rqkt2gp1TJc',
                'fNnsZVoweDM',
                'XCtxl34Z77Y',
              ].map((videoId, i) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={videoId}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        maxWidth: 315,
                        mx: 'auto',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                        border: '1px solid #e5e7eb',
                        '&:hover': { boxShadow: '0 12px 40px rgba(37, 99, 235, 0.2)' },
                      }}
                    >
                      <Box
                        component="iframe"
                        src={`https://www.youtube.com/embed/${videoId}`}
                        title={`Student feedback ${i + 1}`}
                        sx={{
                          display: 'block',
                          width: '100%',
                          aspectRatio: '9/16',
                          border: 'none',
                        }}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                      />
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
}

const AIForEveryoneCourse: React.FC = () => {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedSectionId, setSelectedSectionId] = useState<string>('intro-genai');
  const [expandedIds] = useState<Set<string>>(
    () => new Set(SIDEBAR_SECTIONS.map((s) => s.id))
  );
  const [progress] = useState(15);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const selectSection = (id: string) => {
    setSelectedSectionId(id);
    setActiveId(null);
  };

  const closeDrawer = () => setDrawerOpen(false);
  const handleSectionClick = (id: string) => {
    selectSection(id);
    if (isMobile) closeDrawer();
  };
  const handleTopicClick = (topicId: string, sectionId: string) => {
    setActiveId(topicId);
    setSelectedSectionId(sectionId);
    if (isMobile) closeDrawer();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        minHeight: '100vh',
        pt: { xs: 2, md: 8 },
        pb: 2,
        bgcolor: BG,
        fontFamily: '"Inter", "Poppins", sans-serif',
      }}
    >
      <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: `1px solid ${BORDER}`, bgcolor: CARD_BG, position: 'sticky', top: 0, zIndex: 11 }}>
        <Button startIcon={<ChevronLeft size={18} />} onClick={() => navigate('/')} size="small" sx={{ textTransform: 'none', fontWeight: 600, color: TEXT_DARK }}>Home</Button>
        <IconButton onClick={() => setDrawerOpen(true)} aria-label="Open menu" sx={{ color: TEXT_DARK }}><Menu size={24} /></IconButton>
      </Box>

      <Drawer anchor="left" open={drawerOpen} onClose={closeDrawer} sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 280, maxWidth: '85vw', pt: 2, pb: 2, px: 2, boxSizing: 'border-box' } }}>
        <Typography component="h2" sx={{ fontFamily: MENU_TITLE_FONT, fontSize: '1.2rem', fontWeight: 800, mb: 2, px: 1, color: TEXT_DARK }}>AI for Everyone</Typography>
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {SIDEBAR_SECTIONS.map((section) => {
            const isExpanded = expandedIds.has(section.id);
            return (
              <li key={section.id} style={{ marginBottom: 6 }}>
                <Box onClick={() => handleSectionClick(section.id)} sx={{ py: 1.25, px: 2, cursor: 'pointer', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: 1.25, fontFamily: MENU_SECTION_FONT, fontWeight: 700, fontSize: '0.95rem', color: isExpanded ? PRIMARY : TEXT_DARK, bgcolor: isExpanded ? PRIMARY_LIGHT : 'transparent', borderLeft: `3px solid ${isExpanded ? PRIMARY : 'transparent'}` }}>
                  {isExpanded ? <ChevronDown size={20} strokeWidth={2.5} color={PRIMARY} /> : <ChevronRight size={20} strokeWidth={2.5} color={TEXT_MUTED} />}
                  {section.label}
                </Box>
                {isExpanded && (
                  <Box sx={{ pl: 3.5, pr: 1, py: 0.75, borderLeft: `2px solid ${BORDER}`, ml: 1.5 }}>
                    {section.children.map((child) => (
                      <Box key={child.id} onClick={(e) => { e.stopPropagation(); handleTopicClick(child.id, section.id); }} sx={{ py: 1.1, px: 2, cursor: 'pointer', borderRadius: '10px', fontFamily: SUBMENU_FONT, fontSize: '0.925rem', fontWeight: activeId === child.id ? 700 : 500, bgcolor: activeId === child.id ? PRIMARY_LIGHT : 'transparent', color: activeId === child.id ? PRIMARY : '#475569', borderLeft: activeId === child.id ? `3px solid ${PRIMARY}` : '3px solid transparent' }}>
                        {child.label}
                      </Box>
                    ))}
                  </Box>
                )}
              </li>
            );
          })}
        </Box>
      </Drawer>

      <Box
        sx={{
          width: 280,
          flexShrink: 0,
          bgcolor: CARD_BG,
          borderRight: `1px solid ${BORDER}`,
          position: 'sticky',
          top: 56,
          height: 'calc(100vh - 56px)',
          overflowY: 'auto',
          py: 2.5,
          px: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          zIndex: 10,
          display: { xs: 'none', md: 'block' },
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontFamily: MENU_TITLE_FONT,
            fontSize: '1.35rem',
            fontWeight: 800,
            letterSpacing: '-0.02em',
            background: `linear-gradient(135deg, ${TEXT_DARK} 0%, ${PRIMARY} 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            mb: 2.5,
            px: 1,
            lineHeight: 1.2,
          }}
        >
          AI for Everyone
        </Typography>
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {SIDEBAR_SECTIONS.map((section, sectionIndex) => {
            const isExpanded = expandedIds.has(section.id);
            return (
              <motion.li
                key={section.id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.25, delay: sectionIndex * 0.04 }}
                style={{ marginBottom: 6 }}
              >
                <Box
                  onClick={() => handleSectionClick(section.id)}
                  sx={{
                    py: 1.25,
                    px: 2,
                    cursor: 'pointer',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    fontFamily: MENU_SECTION_FONT,
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    letterSpacing: '0.01em',
                    color: isExpanded ? PRIMARY : TEXT_DARK,
                    bgcolor: isExpanded ? PRIMARY_LIGHT : 'transparent',
                    borderLeft: `3px solid ${isExpanded ? PRIMARY : 'transparent'}`,
                    transition: 'background 0.2s, color 0.2s, border-color 0.2s',
                    '&:hover': {
                      bgcolor: PRIMARY_LIGHT,
                      color: PRIMARY,
                    },
                  }}
                >
                  {isExpanded ? (
                    <ChevronDown size={20} strokeWidth={2.5} color={PRIMARY} />
                  ) : (
                    <ChevronRight size={20} strokeWidth={2.5} color={TEXT_MUTED} />
                  )}
                  {section.label}
                </Box>
                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      style={{ overflow: 'hidden' }}
                    >
                      <Box sx={{ pl: 3.5, pr: 1, py: 0.75, borderLeft: `2px solid ${BORDER}`, ml: 1.5 }}>
                        {section.children.map((child, i) => (
                          <motion.div
                            key={child.id}
                            initial={{ opacity: 0, x: -4 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.05 * i }}
                          >
                            <Box
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTopicClick(child.id, section.id);
                              }}
                              sx={{
                                py: 1.1,
                                px: 2,
                                cursor: 'pointer',
                                borderRadius: '10px',
                                fontFamily: SUBMENU_FONT,
                                fontSize: '0.925rem',
                                fontWeight: activeId === child.id ? 700 : 500,
                                letterSpacing: '0.015em',
                                bgcolor: activeId === child.id ? PRIMARY_LIGHT : 'transparent',
                                color: activeId === child.id ? PRIMARY : '#475569',
                                borderLeft: activeId === child.id ? `3px solid ${PRIMARY}` : '3px solid transparent',
                                transition: 'background 0.2s, color 0.2s',
                                '&:hover': {
                                  bgcolor: activeId === child.id ? PRIMARY_LIGHT : 'rgba(16, 185, 129, 0.08)',
                                  color: activeId === child.id ? PRIMARY_HOVER : TEXT_DARK,
                                },
                              }}
                            >
                              {child.label}
                            </Box>
                          </motion.div>
                        ))}
                      </Box>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.li>
            );
          })}
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          py: { xs: 2, md: 4 },
          px: { xs: 2, sm: 4 },
          maxWidth: 900,
          width: '100%',
        }}
      >
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2, mb: 3 }}>
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              startIcon={<ChevronLeft size={18} />}
              onClick={() => navigate('/')}
              sx={{
                bgcolor: PRIMARY,
                color: '#fff',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '12px',
                px: 2,
                py: 1,
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                '&:hover': { bgcolor: PRIMARY_HOVER },
              }}
            >
              Home
            </Button>
          </motion.div>
        </Box>

        <SectionLandingView
          key={selectedSectionId}
          content={SECTION_LANDING[selectedSectionId] ?? SECTION_LANDING['intro-genai']}
          onStartWithTopic={(topicId) => setActiveId(topicId)}
          progress={progress}
          activeId={activeId}
        />
      </Box>
    </Box>
  );
};

export { HeroRegistrationSection as GenAIEnrollPage };
export default AIForEveryoneCourse;
